import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { aiInstructionCatalog } from '@kirin/api-client';
import {
  CatalogId,
  type CatalogIdType,
  type CatalogResponse,
  type CatalogCategory,
  type CatalogFrameworkState,
  type CatalogFrameworkType,
  type NewCustomCategory,
  type EditCustomCategory,
  type EditBuiltinCategory,
  type CustomCategory,
} from '@kirin/api-client/contracts/ai-instruction-catalog';
import { apiClient } from '@/lib/api-client';

/**
 * KIR-3515 / KIR-3816: AI Instruction Catalog data hooks.
 *
 * Scope is org when `organizationId` is provided, otherwise the current user's
 * personal (account) catalog. `catalog` selects which catalog
 * (`secure_coding` — the four-framework Secure AI Coding Enforcement catalog, or
 * `agent_instructions` — the single-framework AI Agent Instructions catalog);
 * it defaults to `secure_coding` and keys the cache so the two never collide.
 */

function catalogQueryKey(
  organizationId: string | undefined,
  catalog: CatalogIdType
): (string | undefined)[] {
  return ['ai-instruction-catalog', catalog, organizationId ?? 'account'];
}

export function useAiInstructionCatalog(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding,
  enabled = true
): UseQueryResult<CatalogResponse, Error> {
  return useQuery<CatalogResponse, Error>({
    queryKey: catalogQueryKey(organizationId, catalog),
    enabled,
    queryFn: () =>
      aiInstructionCatalog.getAiInstructionCatalog(
        apiClient,
        organizationId,
        catalog
      ),
  });
}

export function useAddCustomCatalogCategory(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding
): UseMutationResult<CustomCategory, Error, NewCustomCategory> {
  const queryClient = useQueryClient();
  return useMutation<CustomCategory, Error, NewCustomCategory>({
    mutationFn: category =>
      aiInstructionCatalog.addCustomCatalogCategory(
        apiClient,
        organizationId,
        category,
        catalog
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: catalogQueryKey(organizationId, catalog),
      });
    },
  });
}

export function useEditCustomCatalogCategory(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding
): UseMutationResult<
  CustomCategory,
  Error,
  { categoryId: string; category: EditCustomCategory }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, category }) =>
      aiInstructionCatalog.editCustomCatalogCategory(
        apiClient,
        organizationId,
        categoryId,
        category,
        catalog
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: catalogQueryKey(organizationId, catalog),
      });
    },
  });
}

export function useRemoveCustomCatalogCategory(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: categoryId =>
      aiInstructionCatalog.removeCustomCatalogCategory(
        apiClient,
        organizationId,
        categoryId,
        catalog
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: catalogQueryKey(organizationId, catalog),
      });
    },
  });
}

export function useEditBuiltinCatalogCategory(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding
): UseMutationResult<
  CatalogCategory,
  Error,
  { builtinSlug: string; edit: EditBuiltinCategory }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ builtinSlug, edit }) =>
      aiInstructionCatalog.editBuiltinCatalogCategory(
        apiClient,
        organizationId,
        builtinSlug,
        edit,
        catalog
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: catalogQueryKey(organizationId, catalog),
      });
    },
  });
}

export function useSetCatalogCategoryEnabled(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding
): UseMutationResult<
  CatalogCategory,
  Error,
  { categoryId: string; enabled: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, enabled }) =>
      aiInstructionCatalog.setCatalogCategoryEnabled(
        apiClient,
        organizationId,
        categoryId,
        enabled,
        catalog
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: catalogQueryKey(organizationId, catalog),
      });
    },
  });
}

export function useSetCatalogFrameworkEnabled(
  organizationId?: string,
  catalog: CatalogIdType = CatalogId.SecureCoding
): UseMutationResult<
  CatalogFrameworkState,
  Error,
  { framework: CatalogFrameworkType; enabled: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ framework, enabled }) =>
      aiInstructionCatalog.setCatalogFrameworkEnabled(
        apiClient,
        organizationId,
        framework,
        enabled,
        catalog
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: catalogQueryKey(organizationId, catalog),
      });
    },
  });
}
